import { SelectChangeEvent } from '@mui/material';
import { addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { useEffect, useMemo, useState } from 'react';

import { Milestone } from '../../../../apollo/__generated__/graphql';
import { useCurrentMilestone } from '../../../../event/hooks/useCurrentMilestone';
import { useEventQuery } from '../../../../event/hooks/useEventQuery';
import { useMilestonesQuery } from '../../../../event/hooks/useMilestonesQuery';
import { useStaffCreateTransactionMutation } from '../../../../transactions/hooks/useStaffCreateTransactionMutation';

export const usePaymentDialog = (
  eventId: string,
  userId: string,
  onClose: () => void,
) => {
  const { data: eventData } = useEventQuery(eventId);
  const { data: dataMilestones } = useMilestonesQuery(eventId);
  const milestones = dataMilestones?.milestones ?? [];
  const currentMilestone = useCurrentMilestone(milestones);
  const [milestone, setMilestone] = useState<Milestone | null>(null);

  const [numberOfDays, setNumberOfDays] = useState<number>(
    milestone?.prices ? milestone.prices.length - 1 : 0,
  );
  const [arrivalDay, setArrivalDay] = useState<number>(0);
  const [isNoAlcool, setIsNoAlcool] = useState<boolean>(false);

  useEffect(() => {
    if (currentMilestone) {
      setMilestone(currentMilestone);
      setNumberOfDays((currentMilestone.prices ?? []).length - 1);
    } else if (milestone === null) {
      setMilestone(milestones[milestones.length - 1] ?? null);
    }
  }, [currentMilestone, milestone, milestones]);

  const { createTransaction } = useStaffCreateTransactionMutation();

  const onNoAlcoolChange = (_: unknown, checked: boolean) => {
    setIsNoAlcool(checked);
  };

  const onSubmit = () => {
    createTransaction({
      eventId,
      isNoAlcool,
      userId,
      milestoneId: milestone?.id,
      arrivalDay,
      numberOfDays: numberOfDays + 1,
    });
    onClose();
  };

  const onMilestoneChange = (event: SelectChangeEvent<string>) => {
    const newMilestone = milestones.find((m) => m.id === event.target.value);

    if (newMilestone) {
      setMilestone(newMilestone);
    }
  };

  const numberOfDaysOptions = useMemo(() => {
    return (milestone?.prices ?? []).map((price) => price.day).reverse();
  }, [milestone]);

  const onNumberOfDaysChange = (value: number | null) => {
    if (value === null) return;

    setNumberOfDays(value);
    if (value === (milestone?.prices ?? []).length - 1) {
      setArrivalDay(0);
    }
  };

  const onArrivalDayChange = (value: number | null) => {
    if (value === null) return;

    setArrivalDay(value);
  };

  const arrivalDayOptions = useMemo(
    () => new Array(eventData?.event.numberOfDays).fill(0).map((_, i) => i),
    [eventData],
  );

  const getDayNameByDay = (day: number) => {
    if (!eventData) {
      return '';
    }
    const date = addDays(eventData.event.startAt, day);
    return format(date, 'eeee', { locale: fr });
  };

  return {
    isNoAlcool,
    milestones,
    milestone,
    onNoAlcoolChange,
    onSubmit,
    onMilestoneChange,
    numberOfDaysOptions,
    numberOfDays,
    onNumberOfDaysChange,
    arrivalDay,
    onArrivalDayChange,
    arrivalDayOptions,
    getDayNameByDay,
  };
};
